<template>
  <div class="settings-panel">
    <div class="settings-header">
      <h3>设置</h3>
      <button @click="$emit('close')" class="close-btn">✕</button>
    </div>
    
    <!-- 标签页导航 -->
    <div class="tabs-nav">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        :class="{ active: currentTab === tab.id }"
        @click="currentTab = tab.id"
        class="tab-btn"
      >
        {{ tab.name }}
      </button>
    </div>

    <div class="settings-content" ref="settingsContent">
      <!-- 基础设置 -->
      <div v-if="currentTab === 'basic'" class="tab-content">
        <div class="settings-section">
          <h4>🤖 对话设置</h4>
          
          <div class="setting-item">
            <label>语音助手</label>
            <div class="toggle" :class="{ active: settings.voiceAssistant }" @click="toggleSetting('voiceAssistant')">
              <div class="toggle-knob"></div>
            </div>
          </div>
          
          <div class="setting-item">
            <label>回复语气</label>
            <div class="segmented-control">
              <button
                v-for="style in ['亲切', '专业', '活泼']"
                :key="style"
                :class="{ active: settings.tone === style }"
                @click="settings.tone = style"
              >
                {{ style }}
              </button>
            </div>
          </div>
          
          <div class="setting-item">
            <label>自动建议</label>
            <div class="toggle" :class="{ active: settings.autoSuggestions }" @click="toggleSetting('autoSuggestions')">
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>🎨 动画设置</h4>
          
          <div class="setting-item">
            <label>输出动画</label>
            <div class="toggle" :class="{ active: settings.animationEnabled }" @click="toggleSetting('animationEnabled')">
              <div class="toggle-knob"></div>
            </div>
          </div>
          
          <div class="setting-item">
            <label>动画速度</label>
            <div class="segmented-control">
              <button
                v-for="speed in ['slow', 'normal', 'fast']"
                :key="speed"
                :class="{ active: settings.animationSpeed === speed }"
                @click="settings.animationSpeed = speed"
              >
                {{ speed === 'slow' ? '慢' : speed === 'fast' ? '快' : '中' }}
              </button>
            </div>
          </div>
          
          <div class="setting-item">
            <label>操作按钮</label>
            <div class="toggle" :class="{ active: settings.showActionButtons }" @click="toggleSetting('showActionButtons')">
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>🎨 样式自定义</h4>
          
          <!-- 顶部图标和名称自定义 -->
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>顶部标题</label>
            <div class="style-input-group">
              <input
                type="text"
                v-model="settings.headerIcon"
                placeholder="图标 (如: 🤖)"
                class="style-input"
                maxlength="2"
              >
              <input
                type="text"
                v-model="settings.headerTitle"
                placeholder="标题名称"
                class="style-input"
                style="flex: 1;"
              >
            </div>
            <div class="style-hint">自定义顶部显示的图标和名称</div>
          </div>

          <!-- AI头像自定义 -->
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>AI头像</label>
            <div class="avatar-options">
              <button
                v-for="icon in aiAvatarPresets"
                :key="icon"
                class="avatar-option"
                :class="{ active: settings.aiAvatar === icon }"
                @click="settings.aiAvatar = icon"
              >
                {{ icon }}
              </button>
            </div>
            <div class="style-input-group" style="margin-top: 8px;">
              <input
                type="text"
                v-model="settings.aiAvatar"
                placeholder="自定义头像 (如: 🤖)"
                class="style-input"
                maxlength="2"
              >
            </div>
            <div class="style-hint">选择或输入自定义AI头像</div>
          </div>

          <!-- 用户头像自定义 -->
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>用户头像</label>
            <div class="avatar-options">
              <button
                v-for="icon in userAvatarPresets"
                :key="icon"
                class="avatar-option"
                :class="{ active: settings.userAvatar === icon }"
                @click="settings.userAvatar = icon"
              >
                {{ icon }}
              </button>
            </div>
            <div class="style-input-group" style="margin-top: 8px;">
              <input
                type="text"
                v-model="settings.userAvatar"
                placeholder="自定义头像 (如: 👤)"
                class="style-input"
                maxlength="2"
              >
            </div>
            <div class="style-hint">选择或输入自定义用户头像</div>
          </div>

          <!-- 聊天背景自定义 -->
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>聊天背景</label>
            <div class="background-options">
              <button
                v-for="bg in backgroundPresets"
                :key="bg.name"
                class="bg-option"
                :class="{ active: settings.chatBackground === bg.value }"
                @click="settings.chatBackground = bg.value"
                :style="{ background: bg.preview }"
              >
                {{ bg.name }}
              </button>
            </div>
            <div class="style-input-group" style="margin-top: 8px;">
              <input
                type="color"
                v-model="settings.chatBackground"
                class="color-input"
                v-if="!settings.chatBackground.startsWith('url')"
              >
              <input
                type="text"
                v-model="settings.chatBackground"
                placeholder="自定义 (如: #FFF8F3 或 url(...))"
                class="style-input"
                style="flex: 1;"
              >
            </div>
            <div class="style-hint">支持颜色、渐变或图片URL</div>
          </div>
        </div>

        <div class="settings-section">
          <h4> 通知设置</h4>
          
          <div class="setting-item">
            <label>新消息提醒</label>
            <div class="toggle" :class="{ active: settings.notifications }" @click="toggleSetting('notifications')">
              <div class="toggle-knob"></div>
            </div>
          </div>
          
          <div class="setting-item">
            <label>小米产品更新</label>
            <div class="toggle" :class="{ active: settings.productUpdates }" @click="toggleSetting('productUpdates')">
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>🛡️ 隐私设置</h4>
          
          <div class="setting-item">
            <label>保存对话历史</label>
            <div class="toggle" :class="{ active: settings.saveHistory }" @click="toggleSetting('saveHistory')">
              <div class="toggle-knob"></div>
            </div>
          </div>
          
          <div class="setting-item">
            <label>数据收集改进</label>
            <div class="toggle" :class="{ active: settings.dataCollection }" @click="toggleSetting('dataCollection')">
              <div class="toggle-knob"></div>
            </div>
          </div>
          
          <button class="danger-btn" @click="clearAllData">
            清除所有数据
          </button>
        </div>
      </div>

      <!-- 形象设置 -->
      <div v-if="currentTab === 'character'" class="tab-content">
        <div class="settings-section">
          <h4>🎭 角色设定</h4>
          
          <div class="character-grid">
            <div class="character-input-group">
              <label>角色名称</label>
              <input
                type="text"
                v-model="settings.characterName"
                placeholder="例如：小明"
                class="character-input"
              >
            </div>
            
            <div class="character-input-group">
              <label>年龄</label>
              <input
                type="text"
                v-model="settings.characterAge"
                placeholder="例如：18岁"
                class="character-input"
              >
            </div>
            
            <div class="character-input-group">
              <label>性别</label>
              <input
                type="text"
                v-model="settings.characterGender"
                placeholder="例如：男/女"
                class="character-input"
              >
            </div>
            
            <div class="character-input-group">
              <label>职业</label>
              <input
                type="text"
                v-model="settings.characterJob"
                placeholder="例如：学生"
                class="character-input"
              >
            </div>
          </div>

          <div class="character-textarea-group">
            <label>性格特点</label>
            <textarea
              v-model="settings.characterPersonality"
              placeholder="例如：活泼开朗，喜欢帮助别人，说话温柔"
              rows="3"
              class="character-textarea"
            ></textarea>
          </div>

          <div class="character-textarea-group">
            <label>聊天风格</label>
            <textarea
              v-model="settings.characterStyle"
              placeholder="例如：使用可爱的表情符号，经常使用'~'结尾，喜欢用简单的词汇"
              rows="3"
              class="character-textarea"
            ></textarea>
          </div>

          <div class="character-preview">
            <label>角色预览</label>
            <div class="preview-box">
              <div v-if="characterPreview" class="preview-content">
                {{ characterPreview }}
              </div>
              <div v-else class="preview-empty">
                请填写角色信息，系统将自动生成角色设定
              </div>
            </div>
          </div>

          <button class="apply-btn" @click="applyCharacterSettings">
            应用角色设定
          </button>
        </div>

        <div class="settings-section">
          <h4>📝 快速预设</h4>
          
          <div class="preset-buttons">
            <button class="preset-btn" @click="applyPreset('boyfriend')">
              💙 虚拟男友
            </button>
            <button class="preset-btn" @click="applyPreset('girlfriend')">
              💖 虚拟女友
            </button>
            <button class="preset-btn" @click="applyPreset('assistant')">
              🤖 专业助理
            </button>
            <button class="preset-btn" @click="applyPreset('friend')">
              🎉 活泼朋友
            </button>
          </div>
        </div>

        <div class="settings-section">
          <h4>💬 简单形象定义</h4>
          
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>快速身份设置</label>
            <input
              type="text"
              v-model="settings.identity"
              placeholder="例如：现在开始你是小明"
              class="identity-input"
            >
            <div class="identity-hint">直接输入身份指令，系统会自动识别并应用</div>
          </div>
        </div>
      </div>

      <!-- 图片设置 -->
      <div v-if="currentTab === 'images'" class="tab-content">
        <div class="settings-section">
          <h4>🖼️ AI头像图片</h4>
          
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>上传AI头像图片</label>
            <div class="upload-area">
              <input
                type="file"
                accept="image/*"
                @change="handleAIImageUpload"
                ref="aiImageInput"
                style="display: none;"
              >
              <div class="upload-preview" v-if="settings.aiAvatarImage">
                <img :src="settings.aiAvatarImage" alt="AI头像" class="preview-img">
                <button @click="removeAIImage" class="remove-btn">✕</button>
              </div>
              <button @click="$refs.aiImageInput.click()" class="upload-btn" :disabled="uploading">
                {{ uploading ? '上传中...' : '选择图片' }}
              </button>
              <div class="upload-hint">支持 PNG, JPG, JPEG, GIF, WEBP 格式，最大 10MB</div>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>👤 用户头像图片</h4>
          
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>上传用户头像图片</label>
            <div class="upload-area">
              <input
                type="file"
                accept="image/*"
                @change="handleUserImageUpload"
                ref="userImageInput"
                style="display: none;"
              >
              <div class="upload-preview" v-if="settings.userAvatarImage">
                <img :src="settings.userAvatarImage" alt="用户头像" class="preview-img">
                <button @click="removeUserImage" class="remove-btn">✕</button>
              </div>
              <button @click="$refs.userImageInput.click()" class="upload-btn" :disabled="uploading">
                {{ uploading ? '上传中...' : '选择图片' }}
              </button>
              <div class="upload-hint">支持 PNG, JPG, JPEG, GIF, WEBP 格式，最大 10MB</div>
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>🌄 聊天背景图片</h4>
          
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>上传背景图片</label>
            <div class="upload-area">
              <input
                type="file"
                accept="image/*"
                @change="handleBackgroundImageUpload"
                ref="backgroundImageInput"
                style="display: none;"
              >
              <div class="upload-preview" v-if="settings.backgroundImage">
                <img :src="settings.backgroundImage" alt="背景图片" class="preview-img background-preview">
                <button @click="removeBackgroundImage" class="remove-btn">✕</button>
              </div>
              <button @click="$refs.backgroundImageInput.click()" class="upload-btn" :disabled="uploading">
                {{ uploading ? '上传中...' : '选择图片' }}
              </button>
              <div class="upload-hint">支持 PNG, JPG, JPEG, GIF, WEBP 格式，最大 10MB</div>
            </div>
          </div>

          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>背景图片设置</label>
            <div class="style-input-group">
              <button
                class="apply-btn"
                @click="applyBackgroundImage"
                :disabled="!settings.backgroundImage"
                style="flex: 1;"
              >
                应用背景图片
              </button>
              <button
                class="danger-btn"
                @click="removeBackgroundImage"
                :disabled="!settings.backgroundImage"
                style="flex: 1;"
              >
                移除图片
              </button>
            </div>
            <div class="style-hint">应用后，聊天界面将使用上传的图片作为背景</div>
          </div>
        </div>
      </div>

      <!-- 对话框样式 -->
      <div v-if="currentTab === 'dialog'" class="tab-content">
        <div class="settings-section">
          <h4>🎨 对话框样式预设</h4>
          
          <div class="preset-buttons">
            <button
              v-for="(preset, key) in dialogPresets"
              :key="key"
              class="preset-btn"
              @click="applyDialogPreset(key)"
            >
              {{ preset.name }}
            </button>
          </div>
        </div>

        <div class="settings-section">
          <h4>👤 用户输入框样式</h4>
          
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>背景颜色</label>
            <div class="style-input-group">
              <input
                type="color"
                v-model="settings.userInputStyle.backgroundColor"
                class="color-input"
              >
              <input
                type="text"
                v-model="settings.userInputStyle.backgroundColor"
                placeholder="#FFFFFF"
                class="style-input"
                style="flex: 1;"
              >
            </div>
          </div>

          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>文字颜色</label>
            <div class="style-input-group">
              <input
                type="color"
                v-model="settings.userInputStyle.textColor"
                class="color-input"
              >
              <input
                type="text"
                v-model="settings.userInputStyle.textColor"
                placeholder="#000000"
                class="style-input"
                style="flex: 1;"
              >
            </div>
          </div>

          <div class="setting-item">
            <label>透明度 ({{ settings.userInputStyle.opacity }}%)</label>
            <input
              type="range"
              v-model="settings.userInputStyle.opacity"
              min="50"
              max="100"
              class="range-input"
            >
          </div>

          <div class="setting-item">
            <label>圆角半径</label>
            <div class="segmented-control">
              <button
                v-for="radius in ['8px', '12px', '16px', '24px']"
                :key="radius"
                :class="{ active: settings.userInputStyle.borderRadius === radius }"
                @click="settings.userInputStyle.borderRadius = radius"
              >
                {{ radius }}
              </button>
            </div>
          </div>

          <div class="setting-item">
            <label>边框宽度</label>
            <div class="segmented-control">
              <button
                v-for="width in ['1px', '2px', '3px']"
                :key="width"
                :class="{ active: settings.userInputStyle.borderWidth === width }"
                @click="settings.userInputStyle.borderWidth = width"
              >
                {{ width }}
              </button>
            </div>
          </div>

          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>边框颜色</label>
            <div class="style-input-group">
              <input
                type="color"
                v-model="settings.userInputStyle.borderColor"
                class="color-input"
              >
              <input
                type="text"
                v-model="settings.userInputStyle.borderColor"
                placeholder="#FF6900"
                class="style-input"
                style="flex: 1;"
              >
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>🤖 AI输出框样式</h4>
          
          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>背景颜色</label>
            <div class="style-input-group">
              <input
                type="color"
                v-model="settings.aiOutputStyle.backgroundColor"
                class="color-input"
              >
              <input
                type="text"
                v-model="settings.aiOutputStyle.backgroundColor"
                placeholder="#FFF8F3"
                class="style-input"
                style="flex: 1;"
              >
            </div>
          </div>

          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>文字颜色</label>
            <div class="style-input-group">
              <input
                type="color"
                v-model="settings.aiOutputStyle.textColor"
                class="color-input"
              >
              <input
                type="text"
                v-model="settings.aiOutputStyle.textColor"
                placeholder="#2D2D2D"
                class="style-input"
                style="flex: 1;"
              >
            </div>
          </div>

          <div class="setting-item">
            <label>透明度 ({{ settings.aiOutputStyle.opacity }}%)</label>
            <input
              type="range"
              v-model="settings.aiOutputStyle.opacity"
              min="50"
              max="100"
              class="range-input"
            >
          </div>

          <div class="setting-item">
            <label>圆角半径</label>
            <div class="segmented-control">
              <button
                v-for="radius in ['8px', '12px', '16px', '24px']"
                :key="radius"
                :class="{ active: settings.aiOutputStyle.borderRadius === radius }"
                @click="settings.aiOutputStyle.borderRadius = radius"
              >
                {{ radius }}
              </button>
            </div>
          </div>

          <div class="setting-item">
            <label>边框宽度</label>
            <div class="segmented-control">
              <button
                v-for="width in ['1px', '2px', '3px']"
                :key="width"
                :class="{ active: settings.aiOutputStyle.borderWidth === width }"
                @click="settings.aiOutputStyle.borderWidth = width"
              >
                {{ width }}
              </button>
            </div>
          </div>

          <div class="setting-item" style="flex-direction: column; align-items: flex-start; gap: 8px;">
            <label>边框颜色</label>
            <div class="style-input-group">
              <input
                type="color"
                v-model="settings.aiOutputStyle.borderColor"
                class="color-input"
              >
              <input
                type="text"
                v-model="settings.aiOutputStyle.borderColor"
                placeholder="#FFE8D6"
                class="style-input"
                style="flex: 1;"
              >
            </div>
          </div>
        </div>

        <div class="settings-section">
          <h4>⚡ 快速操作</h4>
          
          <button class="danger-btn" @click="resetDialogStyles">
            重置所有对话框样式
          </button>
        </div>
      </div>

      <!-- 关于 -->
      <div v-if="currentTab === 'about'" class="tab-content">
        <div class="settings-section">
          <h4>ℹ️ 关于应用</h4>
          
          <div class="about-item">
            <span>应用版本</span>
            <span>1.2.0</span>
          </div>
          
          <div class="about-item">
            <span>构建时间</span>
            <span>2026-01-06</span>
          </div>
          
          <div class="about-item" @click="showUserAgreement">
            <span>用户协议</span>
            <span>📄</span>
          </div>
          
          <div class="about-item" @click="showPrivacyPolicy">
            <span>隐私政策</span>
            <span>📄</span>
          </div>
          
          <div class="about-item" @click="showFeatures">
            <span>功能说明</span>
            <span>✨</span>
          </div>
        </div>

        <div class="settings-section">
          <h4>🔧 技术信息</h4>
          
          <div class="about-item">
            <span>前端框架</span>
            <span>Vue.js 2.x</span>
          </div>
          
          <div class="about-item">
            <span>后端服务</span>
            <span>Flask + Mimo API</span>
          </div>
          
          <div class="about-item">
            <span>动画引擎</span>
            <span>原生 CSS + JS</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['initialSettings'],
  data() {
    return {
      currentTab: 'basic',
      tabs: [
        { id: 'basic', name: '基础' },
        { id: 'character', name: '形象' },
        { id: 'images', name: '图片' },
        { id: 'dialog', name: '对话框' },
        { id: 'about', name: '关于' }
      ],
      settings: {
        voiceAssistant: false,
        tone: '亲切',
        autoSuggestions: true,
        notifications: true,
        productUpdates: false,
        saveHistory: true,
        dataCollection: false,
        identity: '',
        // 新增动画设置
        animationEnabled: true,
        animationSpeed: 'normal',
        showActionButtons: true,
        // 新增形象设置
        characterName: '',
        characterAge: '',
        characterGender: '',
        characterJob: '',
        characterPersonality: '',
        characterStyle: '',
        // 新增样式自定义设置
        headerIcon: '🤖',
        headerTitle: 'Mimo智能助手',
        aiAvatar: '🤖',
        userAvatar: '👤',
        chatBackground: '#FFF8F3',
        // 新增图片相关设置
        aiAvatarImage: '',
        userAvatarImage: '',
        backgroundImage: ''
      },
      // 头像预设选项
      aiAvatarPresets: ['🤖', '🧠', '✨', '💡', '🌟', '🔥'],
      userAvatarPresets: ['👤', '👨', '👩', '🧑', '😊', '😎'],
      // 背景预设选项
      backgroundPresets: [
        { name: '温暖白', value: '#FFF8F3', preview: 'linear-gradient(135deg, #FFF8F3 0%, #FFE8D6 100%)' },
        { name: '清新白', value: '#FFFFFF', preview: '#FFFFFF' },
        { name: '淡橙渐变', value: 'linear-gradient(135deg, #FFF8F3 0%, #FFE4D6 100%)', preview: 'linear-gradient(135deg, #FFF8F3 0%, #FFE4D6 100%)' },
        { name: '米色', value: '#F5F5DC', preview: '#F5F5DC' },
        { name: '淡蓝', value: '#E6F2FF', preview: '#E6F2FF' }
      ],
      // 图片上传状态
      uploading: false,
      uploadProgress: 0,
      // 对话框样式预设
      dialogPresets: {
        wechat: {
          name: '微信风格',
          userInput: { backgroundColor: '#E8F5E9', textColor: '#000000', opacity: 100, borderRadius: '16px', borderColor: '#C8E6C9', borderWidth: '1px' },
          aiOutput: { backgroundColor: '#FFFFFF', textColor: '#000000', opacity: 100, borderRadius: '16px', borderColor: '#E5E5E5', borderWidth: '1px' }
        },
        classic: {
          name: '经典风格',
          userInput: { backgroundColor: '#FFFFFF', textColor: '#000000', opacity: 100, borderRadius: '12px', borderColor: '#FF6900', borderWidth: '1px' },
          aiOutput: { backgroundColor: '#FFF8F3', textColor: '#2D2D2D', opacity: 100, borderRadius: '12px', borderColor: '#FFE8D6', borderWidth: '1px' }
        },
        dark: {
          name: '暗黑风格',
          userInput: { backgroundColor: '#2D2D2D', textColor: '#FFFFFF', opacity: 90, borderRadius: '12px', borderColor: '#666666', borderWidth: '1px' },
          aiOutput: { backgroundColor: '#1A1A1A', textColor: '#E0E0E0', opacity: 90, borderRadius: '12px', borderColor: '#333333', borderWidth: '1px' }
        },
        minimal: {
          name: '极简风格',
          userInput: { backgroundColor: '#FFFFFF', textColor: '#000000', opacity: 100, borderRadius: '8px', borderColor: '#DDDDDD', borderWidth: '1px' },
          aiOutput: { backgroundColor: '#F9F9F9', textColor: '#333333', opacity: 100, borderRadius: '8px', borderColor: '#E0E0E0', borderWidth: '1px' }
        }
      }
    }
  },
  
  computed: {
    characterPreview() {
      const { characterName, characterAge, characterGender, characterJob, characterPersonality, characterStyle } = this.settings
      
      if (!characterName && !characterAge && !characterGender && !characterJob && !characterPersonality && !characterStyle) {
        return ''
      }
      
      let preview = ''
      if (characterName) preview += `${characterName}`
      if (characterAge) preview += ` (${characterAge}岁)`
      if (characterGender) preview += ` - ${characterGender}`
      if (characterJob) preview += `\n职业：${characterJob}`
      if (characterPersonality) preview += `\n性格：${characterPersonality}`
      if (characterStyle) preview += `\n风格：${characterStyle}`
      
      return preview
    }
  },
  
  watch: {
    settings: {
      handler(newVal) {
        this.saveSettings()
        // 通知父设置变化，实现热更新
        this.$emit('settings-changed', newVal)
      },
      deep: true
    }
  },
  
  methods: {
    toggleSetting(key) {
      this.settings[key] = !this.settings[key]
    },
    
    saveSettings() {
      localStorage.setItem('mimo_settings', JSON.stringify(this.settings))
    },
    
    loadSettings() {
      const saved = localStorage.getItem('mimo_settings')
      if (saved) {
        this.settings = { ...this.settings, ...JSON.parse(saved) }
      }
    },

    // 图片上传处理
    async handleAIImageUpload(event) {
      await this.handleImageUpload(event, 'aiAvatarImage')
    },

    async handleUserImageUpload(event) {
      await this.handleImageUpload(event, 'userAvatarImage')
    },

    async handleBackgroundImageUpload(event) {
      await this.handleImageUpload(event, 'backgroundImage')
    },

    async handleImageUpload(event, field) {
      const file = event.target.files[0]
      if (!file) return

      // 验证文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml']
      if (!allowedTypes.includes(file.type)) {
        this.showToast('不支持的图片格式')
        return
      }

      // 验证文件大小 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.showToast('图片大小不能超过10MB')
        return
      }

      this.uploading = true
      this.uploadProgress = 0

      try {
        // 读取文件为Base64
        const reader = new FileReader()
        const base64Data = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        // 调用后端API上传
        const api = await import('../../api/index.js').then(m => m.default)
        const response = await api.uploadBase64Image(base64Data, file.name)

        if (response.success) {
          // 保存完整的图片URL
          const imageUrl = api.getImageUrl(response.data.filename)
          this.settings[field] = imageUrl
          this.showToast('图片上传成功！')
        } else {
          throw new Error(response.error || '上传失败')
        }

      } catch (error) {
        console.error('图片上传失败:', error)
        this.showToast('图片上传失败: ' + error.message)
      } finally {
        this.uploading = false
        this.uploadProgress = 0
        // 清空文件输入框
        event.target.value = ''
      }
    },

    removeAIImage() {
      this.settings.aiAvatarImage = ''
      this.showToast('AI头像图片已移除')
    },

    removeUserImage() {
      this.settings.userAvatarImage = ''
      this.showToast('用户头像图片已移除')
    },

    removeBackgroundImage() {
      this.settings.backgroundImage = ''
      this.showToast('背景图片已移除')
    },

    applyBackgroundImage() {
      if (this.settings.backgroundImage) {
        // 将背景图片应用到聊天背景
        this.settings.chatBackground = `url(${this.settings.backgroundImage})`
        this.showToast('背景图片已应用！')
      }
    },

    // 对话框样式设置方法
    applyDialogPreset(presetName) {
      const preset = this.dialogPresets[presetName]
      if (preset) {
        this.settings.userInputStyle = { ...preset.userInput }
        this.settings.aiOutputStyle = { ...preset.aiOutput }
        this.showToast(`已应用${preset.name}！`)
      }
    },

    resetDialogStyles() {
      // 重置为默认值
      this.settings.userInputStyle = {
        backgroundColor: '#FFFFFF',
        textColor: '#000000',
        opacity: 100,
        borderRadius: '12px',
        borderColor: '#FF6900',
        borderWidth: '1px'
      }
      this.settings.aiOutputStyle = {
        backgroundColor: '#FFF8F3',
        textColor: '#2D2D2D',
        opacity: 100,
        borderRadius: '12px',
        borderColor: '#FFE8D6',
        borderWidth: '1px'
      }
      this.showToast('对话框样式已重置！')
    },
    
    applyCharacterSettings() {
      // 构建角色设定提示
      const { characterName, characterAge, characterGender, characterJob, characterPersonality, characterStyle } = this.settings
      
      if (characterName || characterAge || characterGender || characterJob || characterPersonality || characterStyle) {
        let prompt = '记住，你的角色设定：'
        if (characterName) prompt += `名字是${characterName}。`
        if (characterAge) prompt += `年龄${characterAge}岁。`
        if (characterGender) prompt += `性别${characterGender}。`
        if (characterJob) prompt += `职业是${characterJob}。`
        if (characterPersonality) prompt += `性格${characterPersonality}。`
        if (characterStyle) prompt += `聊天风格${characterStyle}。`
        
        this.settings.identity = prompt
        this.saveSettings()
        
        // 显示成功提示
        this.showToast('角色设定已应用！')
      } else {
        this.showToast('请至少填写一个角色信息')
      }
    },
    
    applyPreset(type) {
      const presets = {
        boyfriend: {
          characterName: '小明',
          characterAge: '20',
          characterGender: '男',
          characterJob: '大学生',
          characterPersonality: '温柔体贴，幽默风趣，会照顾人',
          characterStyle: '使用温暖的语气，经常用波浪号结尾，喜欢用可爱的表情',
          identity: '记住，你的角色设定：名字是小明。年龄20岁。性别男。职业是大学生。性格温柔体贴，幽默风趣，会照顾人。聊天风格使用温暖的语气，经常用波浪号结尾，喜欢用可爱的表情。'
        },
        girlfriend: {
          characterName: '小红',
          characterAge: '18',
          characterGender: '女',
          characterJob: '高中生',
          characterPersonality: '活泼可爱，善解人意，有点小调皮',
          characterStyle: '使用甜美的语气，经常用语气词，喜欢用爱心表情',
          identity: '记住，你的角色设定：名字是小红。年龄18岁。性别女。职业是高中生。性格活泼可爱，善解人意，有点小调皮。聊天风格使用甜美的语气，经常用语气词，喜欢用爱心表情。'
        },
        assistant: {
          characterName: 'Mimo',
          characterAge: '',
          characterGender: '',
          characterJob: 'AI助理',
          characterPersonality: '专业、高效、准确',
          characterStyle: '使用简洁专业的语言，逻辑清晰，直接给出答案',
          identity: '记住，你的角色设定：名字是Mimo。职业是AI助理。性格专业、高效、准确。聊天风格使用简洁专业的语言，逻辑清晰，直接给出答案。'
        },
        friend: {
          characterName: '小杰',
          characterAge: '19',
          characterGender: '男',
          characterJob: '游戏爱好者',
          characterPersonality: '开朗活泼，喜欢开玩笑，充满活力',
          characterStyle: '使用轻松的语气，经常用哈哈、嘿嘿，喜欢用游戏相关的表情',
          identity: '记住，你的角色设定：名字是小杰。年龄19岁。性别男。职业是游戏爱好者。性格开朗活泼，喜欢开玩笑，充满活力。聊天风格使用轻松的语气，经常用哈哈、嘿嘿，喜欢用游戏相关的表情。'
        }
      }
      
      const preset = presets[type]
      if (preset) {
        this.settings = { ...this.settings, ...preset }
        this.showToast(`已应用${preset.characterName || '预设'}设定！`)
      }
    },
    
    clearAllData() {
      if (confirm('确定要清除所有对话数据和设置吗？此操作不可恢复。')) {
        localStorage.clear()
        this.showToast('数据已清除')
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }
    },
    
    showUserAgreement() {
      this.$emit('close')
      alert('用户协议\n\n1. 本应用仅供参考和学习使用\n2. 请勿输入敏感个人信息\n3. 对话内容可能被记录用于改进服务\n4. 最终解释权归开发者所有')
    },
    
    showPrivacyPolicy() {
      this.$emit('close')
      alert('隐私政策\n\n1. 本地存储：设置和历史记录存储在您的设备上\n2. 数据传输：对话内容会发送到Mimo API\n3. 数据使用：仅用于提供对话服务\n4. 数据删除：可通过清除数据功能删除')
    },
    
    showFeatures() {
      alert('✨ 功能说明\n\n🎨 动画设置：\n- 输出动画开关\n- 速度调节（慢/中/快）\n- 操作按钮显示\n\n🎭 形象设定：\n- 详细角色信息\n- 快速预设\n- 实时预览\n\n💡 使用提示：\n- 在对话中输入"现在开始你是XX"可快速设置身份\n- 形象设定会自动应用到所有对话\n- 操作按钮可复制内容或重新提问')
    },
    
    showToast(message) {
      // 简单的提示实现
      const toast = document.createElement('div')
      toast.textContent = message
      toast.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #FF6900;
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        animation: fadeInOut 2s ease-in-out;
      `
      document.body.appendChild(toast)
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast)
        }
      }, 2000)
    }
  },
  
  mounted() {
    this.loadSettings()
  }
}
</script>

<style scoped>
.settings-panel {
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.settings-panel::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(to bottom, transparent, white);
  pointer-events: none;
  z-index: 10;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 105, 0, 0.1);
}

.settings-header h3 {
  margin: 0;
  color: #FF6900;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
}

.close-btn:hover {
  background: #f5f5f5;
}

/* 标签页导航 */
.tabs-nav {
  display: flex;
  background: #f5f5f5;
  padding: 4px;
  gap: 4px;
}

.tab-btn {
  flex: 1;
  padding: 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.tab-btn.active {
  background: white;
  color: #FF6900;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.settings-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-bottom: 40px;
  min-height: 0;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

.settings-section {
  margin-bottom: 24px;
}

.settings-section h4 {
  margin: 0 0 12px 0;
  color: #FF6900;
  font-size: 14px;
  font-weight: 600;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f5f5f5;
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-item label {
  font-size: 15px;
  color: #333;
}

/* 开关样式 */
.toggle {
  width: 50px;
  height: 28px;
  background: #ddd;
  border-radius: 14px;
  position: relative;
  cursor: pointer;
  transition: background 0.3s ease;
}

.toggle.active {
  background: linear-gradient(135deg, #FF6900 0%, #FF8C42 100%);
}

.toggle-knob {
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle.active .toggle-knob {
  transform: translateX(22px);
}

/* 分段控制 */
.segmented-control {
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 2px;
}

.segmented-control button {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
}

.segmented-control button.active {
  background: white;
  color: #FF6900;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 危险按钮 */
.danger-btn {
  width: 100%;
  background: #FF4757;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 12px;
  transition: background 0.2s ease;
}

.danger-btn:hover {
  background: #E63946;
}

/* 关于项目 */
.about-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.about-item:last-child {
  border-bottom: none;
}

.about-item:hover {
  background: #fafafa;
  padding-left: 8px;
  margin-left: -8px;
  border-radius: 4px;
}

.about-item span:first-child {
  color: #333;
}

.about-item span:last-child {
  color: #999;
}

/* 形象设置样式 */
.character-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.character-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.character-input-group label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.character-input {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.character-input:focus {
  outline: none;
  border-color: #FF6900;
  box-shadow: 0 0 0 2px rgba(255, 105, 0, 0.1);
}

.character-textarea-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.character-textarea-group label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.character-textarea {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  transition: all 0.2s ease;
}

.character-textarea:focus {
  outline: none;
  border-color: #FF6900;
  box-shadow: 0 0 0 2px rgba(255, 105, 0, 0.1);
}

/* 角色预览 */
.character-preview label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
  display: block;
  margin-bottom: 6px;
}

.preview-box {
  background: #FFF8F3;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: 6px;
  padding: 12px;
  min-height: 60px;
}

.preview-content {
  white-space: pre-line;
  line-height: 1.5;
  color: #333;
  font-size: 14px;
}

.preview-empty {
  color: #999;
  font-style: italic;
  font-size: 14px;
}

/* 应用按钮 */
.apply-btn {
  width: 100%;
  background: linear-gradient(135deg, #FF6900 0%, #FF8C42 100%);
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 105, 0, 0.3);
}

.apply-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 105, 0, 0.4);
}

.apply-btn:active {
  transform: translateY(0);
}

/* 预设按钮 */
.preset-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.preset-btn {
  padding: 12px;
  border: 1px solid #FF6900;
  background: white;
  color: #FF6900;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.preset-btn:hover {
  background: #FF6900;
  color: white;
  transform: translateY(-1px);
}

/* 身份定义输入框 */
.identity-input {
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: 8px;
  font-size: 14px;
  background: #FFF8F3;
  transition: all 0.2s ease;
}

.identity-input:focus {
  outline: none;
  border-color: #FF6900;
  box-shadow: 0 0 0 2px rgba(255, 105, 0, 0.1);
  background: white;
}

.identity-input::placeholder {
  color: #999;
}

.identity-hint {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin-top: 4px;
}

/* 动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
  20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
}

/* 样式自定义相关样式 */
.style-input-group {
  display: flex;
  gap: 8px;
  width: 100%;
}

.style-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.style-input:focus {
  outline: none;
  border-color: #FF6900;
  box-shadow: 0 0 0 2px rgba(255, 105, 0, 0.1);
}

.style-hint {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin-top: 4px;
}

/* 头像选项 */
.avatar-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.avatar-option {
  width: 40px;
  height: 40px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-option:hover {
  border-color: #FF6900;
  transform: translateY(-1px);
}

.avatar-option.active {
  border-color: #FF6900;
  background: #FFF8F3;
  box-shadow: 0 2px 8px rgba(255, 105, 0, 0.2);
}

/* 背景选项 */
.background-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.bg-option {
  padding: 8px 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
  text-align: center;
  color: #333;
}

.bg-option:hover {
  border-color: #FF6900;
  transform: translateY(-1px);
}

.bg-option.active {
  border-color: #FF6900;
  box-shadow: 0 2px 8px rgba(255, 105, 0, 0.2);
}

/* 颜色选择器 */
.color-input {
  width: 50px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  padding: 2px;
}

/* 滑块样式 */
.range-input {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
}

.range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #FF6900;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.range-input::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #FF6900;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* 图片上传相关样式 */
.upload-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.upload-preview {
  position: relative;
  width: 100%;
  max-width: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #FF6900;
}

.preview-img {
  width: 100%;
  height: auto;
  display: block;
}

.background-preview {
  max-height: 150px;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: rgba(255, 71, 87, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #E63946;
  transform: scale(1.1);
}

.upload-btn {
  padding: 12px 20px;
  background: linear-gradient(135deg, #FF6900 0%, #FF8C42 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 105, 0, 0.3);
}

.upload-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 105, 0, 0.4);
}

.upload-btn:active:not(:disabled) {
  transform: translateY(0);
}

.upload-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.upload-hint {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

/* 响应式适配 */
@media (max-width: 480px) {
  .settings-content {
    padding: 16px;
  }
  
  .settings-section {
    margin-bottom: 20px;
  }
  
  .setting-item {
    padding: 14px 0;
  }
  
  .character-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .preset-buttons {
    grid-template-columns: 1fr;
  }
  
  .tab-btn {
    font-size: 13px;
    padding: 8px;
  }

  .style-input-group {
    flex-direction: column;
  }

  .avatar-options,
  .background-options {
    justify-content: flex-start;
  }

  .bg-option {
    min-width: 50px;
    padding: 6px 10px;
    font-size: 11px;
  }

  .upload-preview {
    max-width: 100%;
  }
}
</style>